<template>
  <form :data-vv-scope="fieldName">
    <div :class="['form-group', {['has-error']: errors.first(fieldName + '-source-form-id', fieldName)}]">
      <label :for="field.name + '-source-form-id'">Source form name*</label>
      <select :id="field.name + '-source-form-id'"
              class="form-control"
              v-model="field.sourceFormId"
              @change="updateFormFields"
              :data-vv-name="fieldName + '-source-form-id'"
              v-validate="'required'">
        <option :value="null">Select...</option>
        <option v-for="form in forms" :value="form.id" v-if="form.id !== formId">{{ form.name }}</option>
      </select>
    </div>

    <div class="row" :class="{'form-group' : field.sourceFormData == 'daterange-field'}" v-show="isSourceFormRangeSelectVisible">
      <div
        :class="{'col-lg-6' : field.sourceFormData == 'daterange', 'col-lg-12' : field.sourceFormData != 'daterange'}">
        <div :class="['form-group', {['has-error']: errors.first(fieldName + '-source-form-data', fieldName)}]">
          <label>Source forms range*</label>
          <select :id="field.name + '-source-form-data'"
                  v-model="field.sourceFormData"
                  class="form-control"
                  :data-vv-name="fieldName + '-source-form-data'"
                  v-validate="'required'">
            <option :value="'all'">All filled forms</option>
            <option :value="'last'">Most recent entry</option>
            <option :value="'daterange'">Completed At Date range</option>
            <option :value="'daterange-field'">Field Date range</option>
          </select>
        </div>
      </div>

      <div class="col-lg-6" v-if="field.sourceFormData == 'daterange-field'">
        <div :class="['form-group', {['has-error']: errors.first(fieldName + '-daterange-field', fieldName)}]">
          <label :for="field.name + '-daterange-field'">Date field*</label>
          <select :id="field.name + '-daterange-field'"
                  class="form-control"
                  v-model="field.dateRangeField"
                  :data-vv-name="fieldName + '-daterange-field'"
                  v-validate="'required'">
            <option :value="null">Select...</option>
            <option v-for="value in formFields" v-if="value.name.includes('date')" :value="value.name">[{{ value.label }}] {{ value.description }}</option>
          </select>
        </div>
      </div>

      <div v-if="['daterange','daterange-field'].includes(field.sourceFormData)" class="col-lg-6">
        <div :class="['form-group', {['has-error']: errors.first(fieldName + '-datepicker', fieldName)}]">
          <label>Select date range*</label>
          <date-picker
            v-model="field.sourceFormDataRange"
            placeholder="Select Date Range"
            lang="en"
            range-separator="-"
            :format="calendarFormat"
            :value-type="'format'"
            :range="true"
            :confirm="false"
            prefix-class="xmx"
            :data-vv-name="fieldName + '-datepicker'"
            v-validate="'required'"
          />
        </div>
      </div>
    </div>

    <div :class="['form-group', {['has-error']: errors.first(fieldName + '-source-field', fieldName)}]">
      <label :for="field.name + '-source-field'">Source field*</label>
      <select :id="field.name + '-source-field'"
              class="form-control"
              v-model="field.sourceFieldName"
              :data-vv-name="fieldName + '-source-field'"
              v-validate="'required'">
        <option :value="null">Select...</option>
        <option v-for="value in formFields" :value="value.name">[{{ value.label }}] {{ value.description }}</option>
      </select>
    </div>

    <div :class="['form-group', {['has-error']: errors.first(fieldName + '-source-field-value', fieldName)}]" v-if="availableOptions.length">
      <label :for="field.name + '-source-field-value'">Filter value*</label>
      <select :id="field.name + '-source-field-value'"
              class="form-control"
              v-model="field.sourceFieldValue"
              :data-vv-name="fieldName + '-source-field-value'"
              v-validate="'required'">
        <option value="__all">[All values]</option>
        <option v-for="opt in availableOptions" :value="opt.label">{{ opt.label }}</option>
      </select>
    </div>


    <div :class="['form-group', {['has-error']: errors.first(fieldName + '-source-field-fn', fieldName)}]" v-if="field.sourceFormData !== 'last'">
      <label :for="field.name + '-source-field-fn'">Source field function*</label>
      <select :id="field.name + '-source-field-fn'"
              class="form-control"
              v-model="field.sourceFieldFunction"
              :data-vv-name="fieldName + '-source-field-fn'"
              v-validate="'required'">
        <option value="count">Count</option>
        <option value="sum" v-if="['number','currency'].includes(field.sourceFieldType)">Sum</option>
        <option value="avg" v-if="['number','currency'].includes(field.sourceFieldType)">Average</option>
      </select>
    </div>

    <div class="form-group" v-if="availableOptions.length">
      <label>Available options</label>
      <div class="table-responsive">
        <table class="table available-options-table">
          <tr>
            <td v-for="opt in availableOptions"><span>{{ opt.label }}</span></td>
          </tr>
        </table>
      </div>
    </div>
  </form>
</template>

<script>

import {mapGetters} from "vuex";
import DatePicker from 'vue2-datepicker';
import {getUserInfo} from "../../../http-common";

export default {
  inject: {
    $validator: '$validator'
  },
  name: 'FormBuilderSharedField',
  props: ['fieldData', 'formId', 'fieldName'],

  data() {
    return {
      doNotEmitUpdate: false,
      loading: false,
      formFields: [],
      availableOptions: [],
      selectedField: null,
      field: {},
      isSourceFormRangeSelectVisible: true,
      fieldDefaults: {
        sourceFormId: null,
        sourceFormData: null,
        sourceFieldName: null,
        sourceFieldFunction: null,
        sourceFormDataRange: [],
        sourceFieldType: '',
        sourceFieldValue: '',
        dateRangeField: null,
        readOnly: false,
      }
    }
  },
  components: {
    DatePicker,
  },
  mounted() {
    this.handleUpdateFieldData(true)
  },
  computed: {
    ...mapGetters({
      'forms': 'getFormsWithSharedFieldsAlphabetical',
      'systemForms': 'getSystemForms'
    }),
    calendarFormat() {
      return getUserInfo('date_format');
    },
    systemFormIds() {
      const systemFormIds = [];

      for (const systemForm in this.systemForms) {
        systemFormIds.push(this.systemForms[systemForm].id)
        systemFormIds.push(this.systemForms[systemForm].id)
      }

      return systemFormIds;
    }
  },
  methods: {
    handleUpdateFieldData(doNotEmitUpdate = false){
      const promise = new Promise((resolve, reject) => {
        if (!this.fieldData){
          reject()
        }

        this.doNotEmitUpdate = doNotEmitUpdate

        this.field = JSON.parse(JSON.stringify(this.fieldData))
        for (const [property, value] of Object.entries(this.fieldDefaults)) {
          if (!this.field.hasOwnProperty(property)) {
            this.$set(this.field, property, value);
          }
        }
        if (this.field.sourceFormId) {
          this.updateFormFields(null,false);
        }

        setTimeout(() => {
          resolve();
        }, 500);
      })

      promise.then(_res => {
        this.doNotEmitUpdate = false;
      })
    },
    updateFormFields(_event = null, shouldResetFormDataSelect = true) {
      let formFields = this.forms.find(item => item.id === this.field.sourceFormId);

      if (formFields) {
        this.formFields = formFields.sharedFields;
      }

      // set sourceFormData to 'last' and hide this select when sourceFormId is any system form
      if (this.systemFormIds.includes(this.field.sourceFormId)) {
        this.isSourceFormRangeSelectVisible = false
        this.field.sourceFormData = 'last'
      } else if (shouldResetFormDataSelect) {
        this.isSourceFormRangeSelectVisible = true
        this.field.sourceFormData = null
      }
    },
    updateFieldParams() {
      let fld = this.formFields.find(item => item.name === this.field.sourceFieldName);

      if (!fld) {
        return;
      }

      if (this.field.sourceFormData === 'last') {
        this.field.sourceFieldFunction = null;
        // this.field.sourceFieldValue = null; // this was causing error that "filter value" select was blocked and user was not able to select anything
      }

      if (this.field.sourceFieldFunction === 'count' && this.field.sourceFieldValue === '__all') {
        this.field.sourceFieldType = 'json';
      } else {
        this.field.sourceFieldType = fld.type;
      }

      this.field.readOnly = (!!fld.sharedReadOnly) || this.field.sourceFormData !== 'last';

      if (fld.hasOwnProperty('values')) {
        this.availableOptions = fld.values;
      } else {
        this.availableOptions = [];
      }
    }
  },
  watch: {
    forms: function () {
      if (!this.field.sourceFormId) {
        this.formFields = [];
        return;
      }
      this.updateFormFields(null, false);
    },
    formFields: function () {
      this.updateFieldParams();
    },
    fieldData: {
      deep: true,
      handler: function (d){
        this.handleUpdateFieldData(true)
      }
    },
    field: {
      deep: true,
      handler: function () {
        if (this.doNotEmitUpdate){
          return
        }
        this.updateFieldParams();
        this.$emit('update', this.field);
      }
    }
  }
}

</script>

<style scoped lang="scss">
.available-options-table {
  td {
    border: 1px #ccc solid;
    padding: 5px 8px;
  }
}
</style>



// WEBPACK FOOTER //
// FormBuilderSharedField.vue?73f3b328